import { useCallback, useMemo, useRef } from 'react';

import { fg } from '@confluence/feature-gating';
import { useIsSSROrHydrating } from '@confluence/ssr-utilities/entry-points/useIsSSROrHydrating.ts';

import {
	observeExtensionDimensions,
	type ExtensionDimensionSource,
} from './ExtensionDimensions.ts';

export const useExtensionDimensions = (
	source: ExtensionDimensionSource | undefined,
	extensionId: string | undefined,
	contextKey: string,
): ((element: HTMLDivElement | null) => void) => {
	const renderServerPlaceholder = useIsSSROrHydrating();
	const cleanup = useRef<(() => void) | undefined>(undefined);
	// The observation belongs to this page context, even when React reuses its DOM node.
	const observationContext = useMemo(
		() => ({ source, extensionId, contextKey }),
		[source, extensionId, contextKey],
	);

	return useCallback(
		(element: HTMLDivElement | null) => {
			const { source, extensionId } = observationContext;
			cleanup.current?.();
			cleanup.current = undefined;
			if (
				!renderServerPlaceholder &&
				element &&
				source &&
				extensionId &&
				fg('confluence_ufo_banner_extension_dimensions')
			) {
				cleanup.current = observeExtensionDimensions(element, source, extensionId);
			}
		},
		[observationContext, renderServerPlaceholder],
	);
};
